import { AfterViewInit, Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ValidationErrors } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { AlertController } from "ionic-angular";
import * as moment from "moment-timezone";
import { commonVar } from "../../../../providers/common-var/common";

/**
 * Generated class for the DatetimeInputComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
  selector: "datetime-input",
  templateUrl: "datetime-input.html",
})
export class DatetimeInputComponent implements OnInit, AfterViewInit {
  // Configuration properties
  @Input() group: FormGroup;
  @Input() config;
  @Input() isDate: boolean;
  @Input() isTime: boolean;
  @Input() initialTimeValue: string = null;

  // Internal data model
  id: string;
  componentId: string;
  label: any;
  value: string;
  innerGroup: FormGroup;
  disabled: boolean;
  readonly: boolean = false;
  required: boolean;

  dateOptional: boolean = false;
  dateUnknown: boolean = false;
  timeOptional: boolean = false;
  timeUnknown: boolean = false;
  startDate: string;
  endDate: string;
  careLocationTZ: string;
  careLocationIANA: string;
  timeZones: Array<{ id: string; name: string; IANA: string; offset: string }>;
  isCreatinine: boolean = false;
  isCreatinineLabel: boolean = false;
  width: boolean = false;
  addBottomPadding: boolean = false;
  hideTimeZone = false;

  get defaultTimeFormat() {
    return this.config.defaultFormat || "HH:mm";
  }

  constructor(
    public formBuilder: FormBuilder,
    public translatePipe: TranslateService,
    private alertCtrl: AlertController
  ) {
    this.timeZones = [
      {
        id: "CST",
        name: "Central Standard Time",
        IANA: "America/Chicago",
        offset: "-06:00",
      },
      {
        id: "CDT",
        name: "Central Daylight Time",
        IANA: "America/Chicago",
        offset: "-05:00",
      },
      {
        id: "EST",
        name: "Eastern Standard Time",
        IANA: "America/New_York",
        offset: "-05:00",
      },
      {
        id: "EDT",
        name: "Eastern Daylight Time",
        IANA: "America/New_York",
        offset: "-04:00",
      },
      {
        id: "MST",
        name: "Mountain Standard Time",
        IANA: "America/Denver",
        offset: "-07:00",
      },
      {
        id: "MDT",
        name: "Mountain Daylight Time",
        IANA: "America/Denver",
        offset: "-06:00",
      },
      {
        id: "PST",
        name: "Pacific Standard Time",
        IANA: "America/Los_Angeles",
        offset: "-08:00",
      },
      {
        id: "PDT",
        name: "Pacific Daylight Time",
        IANA: "America/Los_Angeles",
        offset: "-07:00",
      },
      {
        id: "AKST",
        name: "Alaskan Standard Time",
        IANA: "America/Anchorage",
        offset: "-09:00",
      },
      {
        id: "HST",
        name: "Hawaiian Standard Time",
        IANA: "Pacific/Honolulu",
        offset: "-10:00",
      },
    ];

    // let tempTz = 'PST';
    let tempTz = sessionStorage.getItem("timeZone");
    if (tempTz) {
      let zone = this.timeZones.filter((tz) => tz.id === tempTz);
      if (zone && zone[0] && zone[0].IANA) {
        moment.tz.setDefault(zone[0].IANA);
        this.careLocationIANA = zone[0].IANA;
        this.careLocationTZ = moment.tz(zone[0].IANA).format("z");
      }
    }
  }

  getDate(dateStr): string {
    let date;
    switch (dateStr) {
      case "longAgo": {
        date = moment().subtract("120", "years").format("YYYY-MM-DD");
        break;
      }
      case "decadeFromToday": {
        date = moment().add("10", "years").format("YYYY-MM-DD");
        break;
      }
      case "lastdecadeFromToday": {
        date = moment().subtract("10", "years").format("YYYY-MM-DD");
        break;
      }
      case "patientAdmittedDate": {
        if (this.config.patientAdmittedDate) {
          date = moment(this.config.patientAdmittedDate, "YYYY-MM-DD").format(
            "YYYY-MM-DD"
          );
        } else {
          date = moment().subtract("120", "years").format("YYYY-MM-DD");
        }
        break;
      }
      case "today":
      default:
        date = moment().format("YYYY-MM-DD");
    }

    return date;
  }

  ngOnInit(): void {
    let {
      id,
      label,
      componentId,
      readonly,
      rules,
      initialTimeValue = moment().format(),
    } = this.config;

    this.id = id;
    this.componentId = componentId;
    if (label !== undefined) {
      this.label = label;
    } else {
      this.label = id;
      this.isCreatinineLabel = commonVar.containsWord(
        commonVar.CREATININE_VALUE,
        this.label
      );
    }
    this.label = this.label === "false" ? false : this.label;
    this.readonly = readonly;
    this.value = this.config.default || false;
    // Try to get the default time value for the component if no default information is provided
    this.initialTimeValue = initialTimeValue;
    this.isCreatinine = commonVar.containsWord(
      commonVar.CREATININE_VALUE,
      this.id
    );
    // Outer form state
    this.group.addControl(this.id, this.formBuilder.control({}));

    // set the start and end dates, if any
    // let validators = [];
    if (rules) {
      if (rules.labelHideTimeZone) {
        this.hideTimeZone = true;
      }
      if (rules.required) {
        // validators.push(Validators.required);
        this.required = rules.required;
      }

      if (rules.width) {
        this.width = rules.width;
      }

      if (rules.addBottomPadding) {
        this.addBottomPadding = rules.addBottomPadding;
      }

      if (this.isDate) {
        this.dateOptional = rules.dateOptional;

        if (rules.startDate) {
          this.startDate = this.getDate(rules.startDate);
        }
        if (rules.endDate) {
          this.endDate = this.getDate(rules.endDate);
        } else {
          this.endDate = this.getDate("decadeFromToday");
        }
      }
      if (this.isTime) {
        this.timeOptional = rules.timeOptional;
      }
    }

    // Inner form state;
    this.innerGroup = this.formBuilder.group({});
    if (this.isDate) {
      this.innerGroup.addControl("date", this.formBuilder.control(""));
      if (this.dateOptional) {
        this.innerGroup.addControl(
          "dateUnknown",
          this.formBuilder.control(false)
        );
      }
    }
    if (this.isTime) {
      this.innerGroup.addControl("time", this.formBuilder.control(this.value));
      if (this.timeOptional) {
        this.innerGroup.addControl(
          "timeUnknown",
          this.formBuilder.control(false)
        );
      }
    }

    // if ( this.isDate ) {
    //   let dateValidators: ValidatorFn = Validators.compose(validators);
    //   this.innerGroup.controls['date'].setValidators( dateValidators );
    // }
    // if ( this.isTime ) {
    //   let timeValidators: ValidatorFn = Validators.compose(validators);
    //   this.innerGroup.controls['time'].setValidators( timeValidators );
    // }

    this.innerGroup.valueChanges.subscribe(
      this.onInnerGroupValueChanged.bind(this)
    );
    this.group.controls[this.id].valueChanges.subscribe(
      this.importData.bind(this)
    );

    const initValue = this.group.controls[this.id].value;
    if (initValue) {
      this.importData(initValue);
    }

    this.toggleControlDisable();
  }

  ngAfterViewInit(): void {
    this.group.controls[this.id].setErrors(this.getErrors());

    // Errors can get lost when manually set
    // This is an overzealous approach to guarantee they get maintained
    this.group.valueChanges.subscribe((data) => {
      this.group.controls[this.id].setErrors(this.getErrors());
    });
  }

  onInnerGroupValueChanged() {
    this.toggleUnknownCheckboxes();

    if (
      !this.compareStates(
        this.group.controls[this.id].value,
        this.generateInnerValue()
      )
    ) {
      this.exportInnerValue();
    }
  }

  private importData(data) {
    this.importOuterValue(data);
    this.toggleControlDisable();
    this.toggleUnknownCheckboxes();
  }

  private toggleUnknownCheckboxes() {
    let dateControl = this.innerGroup.controls["date"];
    let timeControl = this.innerGroup.controls["time"];
    // console.log( "Toggling unkowns: ", this.id, dateControl, timeControl );

    if (this.isDate) {
      let dateUnknown =
        this.dateOptional && this.innerGroup.controls["dateUnknown"].value;
      this.dateUnknown = dateUnknown;
      if (dateUnknown && dateControl.enabled) {
        dateControl.disable({ emitEvent: false });
        dateControl.setValue("", { emitEvent: false });
      } else if (!dateUnknown && dateControl.disabled) {
        if (!this.readonly && !this.disabled) {
          dateControl.enable({ emitEvent: false });
        }
      }
    }

    if (this.isTime) {
      let timeUnknown =
        this.timeOptional && this.innerGroup.controls["timeUnknown"].value;
      this.timeUnknown = timeUnknown;
      if (timeUnknown && timeControl.enabled) {
        timeControl.disable({ emitEvent: false });
        timeControl.setValue("", { emitEvent: false });
      } else if (!timeUnknown && timeControl.disabled) {
        if (!this.readonly && !this.disabled) {
          timeControl.enable({ emitEvent: false });
        }
      }
    }
  }

  exportInnerValue(): void {
    let output = this.generateInnerValue();

    this.group.controls[this.id].setValue(output); //, {emitEvent: false});
    this.group.controls[this.id].markAsDirty();

    this.group.controls[this.id].setErrors(this.getErrors());
  }

  // Sync from outer form to inner form
  importOuterValue(data: object): void {
    let innerValue = this.generateInnerValue();
    // debugger;
    if (data === null) {
      let emptyVal = {};
      if (this.isDate) {
        emptyVal["date"] = "";
        if (this.dateOptional) {
          emptyVal["dateUnknown"] = false;
        }
      }
      if (this.isTime) {
        emptyVal["time"] = "";
        if (this.timeOptional) {
          emptyVal["timeUnknown"] = false;
        }
      }

      this.innerGroup.setValue(emptyVal, { emitEvent: false });
    } else if (data !== undefined && innerValue != data) {
      //  console.log( "LOADING DATETIME: ", data );
      // this is where the data is loading in and needs to be converted to a format for the date/time inputs
      // TODO do conversion somewhere here

      if (this.dateOptional) {
        let dateUnknown = this.innerGroup.controls["dateUnknown"].value;
        if (dateUnknown) {
          data["date"] = "unknown";
        }
      }

      if (this.timeOptional) {
        let timeUnknown = this.innerGroup.controls["timeUnknown"].value;
        if (timeUnknown) {
          data["time"] = "unknown";
        }
      }

      if (this.isDate && this.isTime) {
        let tz = this.careLocationIANA
          ? this.careLocationIANA
          : moment.tz.guess();
        if (
          data["date"] !== "unknown" &&
          data["date"] !== null &&
          data["date"] !== undefined &&
          data["time"] !== "unknown" &&
          data["time"] !== null &&
          data["time"] !== undefined
        ) {
          let dateTimeStr = moment(
            data["date"] + "T" + data["time"],
            "YYYYMMDDTHHmmssZZ"
          ).format("YYYYMMDDTHHmmssZZ");
          let utcStamp = moment(dateTimeStr, "YYYYMMDDTHHmmssZZ")
            .tz(tz)
            .format("YYYYMMDDTHHmmssZZ");
          data["date"] = moment(utcStamp, "YYYYMMDDTHHmmssZZ").format(
            "YYYYMMDD"
          );
          data["time"] = moment(utcStamp, "YYYYMMDDTHHmmssZZ").format(
            "HHmmssZZ"
          );
          // console.log( "CONVERTED DATETIME: ", data );
        }
      }

      let value = this.convertOuterValue(data);
      // console.log("Setting datetime inner value: ", value);
      this.innerGroup.setValue(value, { emitEvent: false });
    }
  }

  generateInnerValue(): object {
    let output;
    if (this.isDate && this.isTime) {
      output = { date: undefined, time: undefined };

      let tz = this.careLocationIANA
        ? this.careLocationIANA
        : moment.tz.guess();
      let dateUnknown = this.dateOptional
        ? this.innerGroup.controls["dateUnknown"].value
        : undefined;
      let timeUnknown = this.timeOptional
        ? this.innerGroup.controls["timeUnknown"].value
        : undefined;
      if (!dateUnknown && !timeUnknown) {
        if (
          output["date"] !== null &&
          output["date"] !== undefined &&
          output["time"] !== null &&
          output["time"] !== undefined
        ) {
          let dateTimeStr = moment(
            output["date"] + "T" + output["time"],
            "YYYYMMDDTHHmmssZZ"
          ).format("YYYYMMDDTHHmmssZZ");
          let utcStamp = moment(dateTimeStr, "YYYYMMDDTHHmmssZZ")
            .tz(tz)
            .format("YYYYMMDDTHHmmssZZ");
          output["date"] = moment(utcStamp, "YYYYMMDDTHHmmssZZ").format(
            "YYYYMMDD"
          );
          output["time"] = moment(utcStamp, "YYYYMMDDTHHmmssZZ").format(
            "HHmmssZZ"
          );
        } else {
          output["date"] = moment(
            this.innerGroup.controls["date"].value,
            "YYYY-MM-DD"
          ).format("YYYYMMDD");
          output["time"] = moment(
            this.innerGroup.controls["time"].value,
            "HH:mm"
          ).format("HHmmssZZ");
        }
      } else {
        if (!dateUnknown) {
          let date = moment(
            this.innerGroup.controls["date"].value,
            "YYYY-MM-DD"
          ).format("YYYYMMDD");
          output["date"] = date;
        }

        if (!timeUnknown) {
          let time = moment(
            this.innerGroup.controls["time"].value,
            "HH:mm"
          ).format("HHmmssZZ");
          output["time"] = time;
        }
      }

      if (this.dateOptional) {
        let dateUnknown = this.innerGroup.controls["dateUnknown"].value;
        if (dateUnknown) {
          output["date"] = "unknown";
        }
      }
      if (this.timeOptional) {
        let timeUnknown = this.innerGroup.controls["timeUnknown"].value;
        if (timeUnknown) {
          output["time"] = "unknown";
        }
      }
    } else if (this.isDate) {
      output = { date: undefined };

      let date = moment(
        this.innerGroup.controls["date"].value,
        "YYYY-MM-DD"
      ).format("YYYYMMDD");
      //date = moment(date, "YYYYMMDD").format("YYYYMMDD");
      if (this.dateOptional) {
        let dateUnknown = this.innerGroup.controls["dateUnknown"].value;
        if (dateUnknown) {
          date = "unknown";
        }
      }

      output.date = date;
    } else {
      output = { time: undefined };
      let time = moment(this.innerGroup.controls["time"].value, "HH:mm").format(
        "HHmmssZZ"
      );
      //time = moment(time, "HHmmssZZ").format("HHmmssZZ");
      if (this.timeOptional) {
        let timeUnknown = this.innerGroup.controls["timeUnknown"].value;
        if (timeUnknown) {
          time = "unknown";
        }
      }

      output.time = time;
    }

    if (this.isDate) {
      if (output.date == undefined || output.date === "Invalid date") {
        output.date = null;
      }
    }
    if (this.isTime) {
      if (output.time == undefined || output.time === "Invalid date") {
        output.time = null;
      }
    }

    return output;
  }

  convertOuterValue(data) {
    if (data === null) {
      return null;
    }

    let date = data["date"];
    let time = data["time"];

    // console.log( "Date: ", date );
    // console.log( "Time: ", time );

    let value = {};

    if (this.isDate) {
      if (this.dateOptional) {
        value["dateUnknown"] = date == "unknown";
        if (!value["dateUnknown"]) {
          value["date"] = moment(date, "YYYYMMDD").format("YYYY-MM-DD");
        } else {
          value["date"] = "";
        }
      } else {
        value["date"] = moment(date, "YYYYMMDD").format("YYYY-MM-DD");
      }
    }

    if (this.isTime) {
      if (this.timeOptional) {
        value["timeUnknown"] = time == "unknown";
        if (!value["timeUnknown"]) {
          value["time"] = moment(time, "HHmmssZZ").format("HH:mm");
        } else {
          value["time"] = "";
        }
      } else {
        if (time) {
          // Use parseZone to preserve the original timezone offset embedded in the
          // stored value (HHmmssZZ format). This ensures the time displays exactly
          // as it was entered, regardless of DST changes or the viewer's timezone.
          value["time"] = moment.parseZone(time, "HHmmssZZ").format("HH:mm");
        } else {
          value["time"] = "";
        }
      }
    } else {
    }
    return value;
  }

  toggleControlDisable(): void {
    // console.log( "Toggling control disabled! Inner state: ", this.disabled );
    let outerDisabled = this.group.controls[this.id].disabled;
    // console.log( "Outer state: ", outerDisabled );
    if (outerDisabled !== this.disabled) {
      this.disabled = outerDisabled;
      if (this.disabled || this.readonly) {
        this.innerGroup.disable({ emitEvent: false });
      } else {
        this.innerGroup.enable({ emitEvent: false });
      }
    }
  }

  compareStates(a, b) {
    if (!a || !b) {
      return a == b;
    }

    var aProps = Object.getOwnPropertyNames(a);
    var bProps = Object.getOwnPropertyNames(b);
    if (aProps.length != bProps.length) {
      return false;
    }

    for (var i = 0; i < aProps.length; i++) {
      var propName = aProps[i];

      if (a[propName] !== b[propName]) {
        if (propName == "time" && this.isDate && this.isTime) {
          let momentA =
            a["time"] !== "unknown"
              ? moment(a[propName], "HHmmssZZ")
              : a["time"];
          let momentB =
            b["time"] !== "unknown"
              ? moment(b[propName], "HHmmssZZ")
              : b["time"];
          if (momentA.toString() === momentB.toString()) {
            continue;
          }
        }

        return false;
      }
    }
    return true;
  }

  validateFieldsComplete(data: object): boolean {
    // console.log( "Checking if complete: ", data );
    if (null === data) {
      return false;
    }

    if (
      this.isDate &&
      (data["date"] == null ||
        data["date"] == undefined ||
        data["date"] == "Invalid date")
    ) {
      return false;
    }

    if (
      this.isTime &&
      (data["time"] == null ||
        data["time"] == undefined ||
        data["time"] == "Invalid date")
    ) {
      return false;
    }

    return true;
  }

  getErrors(): ValidationErrors {
    let errors = {};
    if (this.config.rules) {
      if (
        this.config.rules.required &&
        !this.validateFieldsComplete(this.generateInnerValue())
      ) {
        errors["required"] = true;
      }
    }

    if (this.config) {
      if (this.config.rules) {
        if (this.config.rules.closeVisit) {
          errors = this.assessmentCompletionValidation(errors);
        }
      }
    }

    return Object.keys(errors).length == 0 ? undefined : errors;
  }

  assessmentCompletionValidation(errors): any {
    var startDateGrp = this.group.controls["assessmentStartDate"];
    var startTimeGrp = this.group.controls["assessmentStartTime"];
    var endDateGrp = this.group.controls["assessmentEndDate"];
    var endTimeGrp = this.group.controls["assessmentEndTime"];

    if (startDateGrp && startTimeGrp && endDateGrp && endTimeGrp) {
      if (
        startDateGrp.value &&
        startTimeGrp.value &&
        endDateGrp.value &&
        endTimeGrp.value
      ) {
        let startDate = moment(startDateGrp.value.date, "YYYYMMDD").format(
          "YYYY-MM-DD"
        );
        let startTime = moment(startTimeGrp.value.time, "hhmmss-Z").format(
          "HH:mm:ss"
        );
        let endDate = moment(endDateGrp.value.date, "YYYYMMDD").format(
          "YYYY-MM-DD"
        );
        let endTime = moment(endTimeGrp.value.time, "hhmmss-Z").format(
          "HH:mm:ss"
        );

        let start = startDate + "T" + startTime;
        let end = endDate + "T" + endTime;

        var mStart = moment(start);
        var mEnd = moment(end);
        var val = mEnd.isSameOrBefore(mStart);

        if (val && this.id == "assessmentEndTime") errors["dategreater"] = true;

        var duration = moment.duration(mEnd.diff(mStart));
        var hours = duration.asHours();
        if (hours > 8) {
          this.displayConfirmationForVisitDuration();
        }
      }
    }

    return errors;
  }

  private get hasNoSessionKeyForConfirmationDisplay() {
    return !sessionStorage.getItem("key");
  }

  private setSessionKeyForConfirmationDisplay() {
    // This is added to show alert only one time avoid showing multiple times. We set this key whenever
    // we want to display the confirmation for duration. We remove this once the confirmation is displayed.
    sessionStorage.setItem("key", "DatePop");
  }

  private displayConfirmationForVisitDuration() {
    if (this.hasNoSessionKeyForConfirmationDisplay) {
      this.setSessionKeyForConfirmationDisplay();
      let self = this;
      const alert = this.alertCtrl.create({
        title: this.translatePipe.instant("timegreaterthaneight"),
        buttons: [
          {
            text: "Proceed",
            handler() {
              alert.dismiss().then(() => {
                sessionStorage.removeItem("key");
              });
              return false;
            },
          },
          {
            text: "Cancel",
            handler() {
              alert.dismiss().then(() => {
                sessionStorage.removeItem("key");
                self.group.controls["assessmentStartDate"].setValue(null);
                self.group.controls["assessmentStartTime"].setValue(null);
                self.group.controls["assessmentEndDate"].setValue(null);
                self.group.controls["assessmentEndTime"].setValue(null);
              });
              return false;
            },
          },
        ],
      });
      setTimeout(() => {
        alert.present();
      }, 200);
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/inputs/datetime-input/datetime-input.ts